import { SimNaoEnum } from 'components/form'
import { RacaCorSelectModel } from 'components/form/field/select/RacaCorSelectField'
import createDecorator, { Calculation } from 'final-form-calculate'
import { NacionalidadeEnum, RacaCorDbEnum, SexoEnum } from 'graphql/types.generated'
import { MetaRoot } from 'util/metaPath'

import { CidadaoFciFormModel } from './model-cidadaoFci'

const createEtniaCalculator = (meta: MetaRoot<CidadaoFciFormModel>): Calculation[] => [
  {
    field: meta?.dadosPessoais?.racaCor.absolutePath(),
    updates: {
      [meta?.dadosPessoais?.etnia?.absolutePath()]: (racaCor: RacaCorSelectModel, allValues: CidadaoFciFormModel) =>
        racaCor?.value !== RacaCorDbEnum.INDIGENA ? null : allValues?.dadosPessoais?.etnia,
    },
  },
]

const createNomeGenitoresCalculator = (meta: MetaRoot<CidadaoFciFormModel>): Calculation[] => [
  {
    field: meta?.dadosPessoais?.desconheceNomeMae.absolutePath(),
    updates: {
      [meta?.dadosPessoais?.nomeMae.absolutePath()]: (desconheceNomeMae: boolean, allValues: CidadaoFciFormModel) =>
        desconheceNomeMae ? null : allValues?.dadosPessoais?.nomeMae,
    },
  },
  {
    field: meta?.dadosPessoais?.desconheceNomePai.absolutePath(),
    updates: {
      [meta?.dadosPessoais?.nomePai.absolutePath()]: (desconheceNomePai: boolean, allValues: CidadaoFciFormModel) =>
        desconheceNomePai ? null : allValues?.dadosPessoais?.nomePai,
    },
  },
]

const createIdentidadeGeneroCalculator = (meta: MetaRoot<CidadaoFciFormModel>): Calculation[] => [
  {
    field: meta?.dadosPessoais?.desejaInformarIdentidadeGenero.absolutePath(),
    updates: {
      [meta?.dadosPessoais?.identidadeGenero.absolutePath()]: (
        desejaInformarIdentidadeGenero: SimNaoEnum,
        allValues: CidadaoFciFormModel
      ) => (desejaInformarIdentidadeGenero !== SimNaoEnum.SIM ? null : allValues?.dadosPessoais?.identidadeGenero),
    },
  },
]

const createOrientacaoSexualCalculator = (meta: MetaRoot<CidadaoFciFormModel>): Calculation[] => [
  {
    field: meta?.dadosPessoais?.desejaInformarOrientacaoSexual.absolutePath(),
    updates: {
      [meta?.dadosPessoais?.orientacaoSexual.absolutePath()]: (
        desejaInformarOrientacaoSexual: SimNaoEnum,
        allValues: CidadaoFciFormModel
      ) => (desejaInformarOrientacaoSexual !== SimNaoEnum.SIM ? null : allValues?.dadosPessoais?.orientacaoSexual),
    },
  },
]

const createNacionalidadeCalculator = (meta: MetaRoot<CidadaoFciFormModel>): Calculation[] => [
  {
    field: meta?.dadosPessoais?.nacionalidadeCidadao?.nacionalidade.absolutePath(),
    updates: {
      [meta?.dadosPessoais?.nacionalidadeCidadao?.municipioNascimento.absolutePath()]: (
        nacionalidadeEnum: NacionalidadeEnum,
        allValues: CidadaoFciFormModel
      ) =>
        nacionalidadeEnum !== NacionalidadeEnum.BRASILEIRA
          ? null
          : allValues?.dadosPessoais?.nacionalidadeCidadao?.municipioNascimento,

      [meta?.dadosPessoais?.nacionalidadeCidadao?.portariaNaturalizacao.absolutePath()]: (
        nacionalidadeEnum: NacionalidadeEnum,
        allValues: CidadaoFciFormModel
      ) =>
        nacionalidadeEnum !== NacionalidadeEnum.NATURALIZADA
          ? null
          : allValues?.dadosPessoais?.nacionalidadeCidadao?.portariaNaturalizacao,
      [meta?.dadosPessoais?.nacionalidadeCidadao?.dataNaturalizacao.absolutePath()]: (
        nacionalidadeEnum: NacionalidadeEnum,
        allValues: CidadaoFciFormModel
      ) =>
        nacionalidadeEnum !== NacionalidadeEnum.NATURALIZADA
          ? null
          : allValues?.dadosPessoais?.nacionalidadeCidadao?.dataNaturalizacao,

      [meta?.dadosPessoais?.nacionalidadeCidadao?.paisNascimento.absolutePath()]: (
        nacionalidadeEnum: NacionalidadeEnum,
        allValues: CidadaoFciFormModel
      ) =>
        nacionalidadeEnum !== NacionalidadeEnum.ESTRANGEIRA
          ? null
          : allValues?.dadosPessoais?.nacionalidadeCidadao?.paisNascimento,
      [meta?.dadosPessoais?.nacionalidadeCidadao?.dataEntradaPais.absolutePath()]: (
        nacionalidadeEnum: NacionalidadeEnum,
        allValues: CidadaoFciFormModel
      ) =>
        nacionalidadeEnum !== NacionalidadeEnum.ESTRANGEIRA
          ? null
          : allValues?.dadosPessoais?.nacionalidadeCidadao?.dataEntradaPais,
    },
  },
]

const createObitoCalculator = (meta: MetaRoot<CidadaoFciFormModel>): Calculation[] => [
  {
    field: meta?.dadosPessoais?.faleceu?.absolutePath(),
    updates: {
      [meta?.dadosPessoais?.dataObito?.absolutePath()]: (faleceu: Boolean, allValues: CidadaoFciFormModel) =>
        faleceu !== true ? null : allValues?.dadosPessoais?.dataObito,
      [meta?.dadosPessoais?.numeroDeclaracaoObito?.absolutePath()]: (
        faleceu: Boolean,
        allValues: CidadaoFciFormModel
      ) => (faleceu !== true ? null : allValues?.dadosPessoais?.numeroDeclaracaoObito),
    },
  },
]

const createInformacoesSociodemograficasCalculator = (meta: MetaRoot<CidadaoFciFormModel>): Calculation[] => [
  {
    field: meta?.informacoesSociodemograficas?.possuiDeficiencia.absolutePath(),
    updates: {
      [meta?.informacoesSociodemograficas.deficiencias.absolutePath()]: (
        possuiDeficiencia: SimNaoEnum,
        allValues: CidadaoFciFormModel
      ) => (possuiDeficiencia !== SimNaoEnum.SIM ? null : allValues.informacoesSociodemograficas.deficiencias),
    },
  },
  {
    field: meta?.informacoesSociodemograficas?.isMembroComunidadeTradicional.absolutePath(),
    updates: {
      [meta?.informacoesSociodemograficas.povoComunidadeTradicional.absolutePath()]: (
        isMembroComunidadeTradicional: SimNaoEnum,
        allValues: CidadaoFciFormModel
      ) =>
        isMembroComunidadeTradicional !== SimNaoEnum.SIM
          ? null
          : allValues.informacoesSociodemograficas.povoComunidadeTradicional,
    },
  },
]

const createCondicoesSaudeAutorreferidasCalculator = (meta: MetaRoot<CidadaoFciFormModel>): Calculation[] => [
  {
    field: meta?.dadosPessoais.sexo.absolutePath(),
    updates: {
      [meta?.condicoesSaudeAutorreferidas.stGestante.absolutePath()]: (sexo: string, allValues: CidadaoFciFormModel) =>
        sexo === SexoEnum.MASCULINO ? null : allValues.condicoesSaudeAutorreferidas?.stGestante,

      [meta?.condicoesSaudeAutorreferidas.maternidadeReferencia.absolutePath()]: (
        sexo: string,
        allValues: CidadaoFciFormModel
      ) => (sexo === SexoEnum.MASCULINO ? null : allValues.condicoesSaudeAutorreferidas?.maternidadeReferencia),
    },
  },
  {
    field: meta?.condicoesSaudeAutorreferidas.stGestante.absolutePath(),
    updates: {
      [meta?.condicoesSaudeAutorreferidas.maternidadeReferencia.absolutePath()]: (
        stGestante: SimNaoEnum,
        allValues: CidadaoFciFormModel
      ) => (stGestante !== SimNaoEnum.SIM ? null : allValues.condicoesSaudeAutorreferidas?.maternidadeReferencia),
    },
  },
  {
    field: meta?.condicoesSaudeAutorreferidas.stDoencaCardiaca.absolutePath(),
    updates: {
      [meta?.condicoesSaudeAutorreferidas.doencaCardiaca.absolutePath()]: (
        stDoencaCardiaca: SimNaoEnum,
        allValues: CidadaoFciFormModel
      ) => (stDoencaCardiaca !== SimNaoEnum.SIM ? null : allValues.condicoesSaudeAutorreferidas?.doencaCardiaca),
    },
  },
  {
    field: meta?.condicoesSaudeAutorreferidas.stDoencaRespiratoria.absolutePath(),
    updates: {
      [meta?.condicoesSaudeAutorreferidas.doencasRespiratorias.absolutePath()]: (
        stDoencaRespiratoria: SimNaoEnum,
        allValues: CidadaoFciFormModel
      ) =>
        stDoencaRespiratoria !== SimNaoEnum.SIM ? null : allValues.condicoesSaudeAutorreferidas?.doencasRespiratorias,
    },
  },
  {
    field: meta?.condicoesSaudeAutorreferidas.stProblemaRins.absolutePath(),
    updates: {
      [meta?.condicoesSaudeAutorreferidas.doencaRins.absolutePath()]: (
        stProblemaRins: SimNaoEnum,
        allValues: CidadaoFciFormModel
      ) => (stProblemaRins !== SimNaoEnum.SIM ? null : allValues.condicoesSaudeAutorreferidas?.doencaRins),
    },
  },
  {
    field: meta?.condicoesSaudeAutorreferidas.stInternacaoUltimos12Meses.absolutePath(),
    updates: {
      [meta?.condicoesSaudeAutorreferidas.causaInternacaoUltimos12Meses.absolutePath()]: (
        stInternacaoUltimos12Meses: SimNaoEnum,
        allValues: CidadaoFciFormModel
      ) =>
        stInternacaoUltimos12Meses !== SimNaoEnum.SIM
          ? null
          : allValues.condicoesSaudeAutorreferidas?.causaInternacaoUltimos12Meses,
    },
  },
  {
    field: meta?.condicoesSaudeAutorreferidas.stPlantasMedicinais.absolutePath(),
    updates: {
      [meta?.condicoesSaudeAutorreferidas.plantasMedicinais.absolutePath()]: (
        stPlantasMedicinais: SimNaoEnum,
        allValues: CidadaoFciFormModel
      ) => (stPlantasMedicinais !== SimNaoEnum.SIM ? null : allValues.condicoesSaudeAutorreferidas?.plantasMedicinais),
    },
  },
]

const createSituacaoRuaCalculator = (meta: MetaRoot<CidadaoFciFormModel>): Calculation[] => {
  return [
    {
      field: meta?.statusSituacaoRua.absolutePath(),
      updates: {
        [meta?.situacaoRua?.absolutePath()]: (statusSituacaoRua: boolean, allValues: CidadaoFciFormModel) =>
          statusSituacaoRua !== true ? null : allValues.situacaoRua,
      },
    },
    {
      field: meta?.situacaoRua?.possuiAcessoHigienePessoal.absolutePath(),
      updates: {
        [meta?.situacaoRua?.higienePessoal?.absolutePath()]: (
          possuiHigienePessoal: SimNaoEnum,
          allValues: CidadaoFciFormModel
        ) =>
          allValues?.situacaoRua && possuiHigienePessoal !== SimNaoEnum.SIM
            ? null
            : allValues?.situacaoRua?.higienePessoal,
      },
    },
    {
      field: meta?.situacaoRua?.stAcompanhadoOutraInstituicao.absolutePath(),
      updates: {
        [meta?.situacaoRua?.instituicaoAcompanhamento?.absolutePath()]: (
          stAcompanhadoOutraInstituicao: SimNaoEnum,
          allValues: CidadaoFciFormModel
        ) =>
          allValues?.situacaoRua && stAcompanhadoOutraInstituicao !== SimNaoEnum.SIM
            ? null
            : allValues?.situacaoRua?.instituicaoAcompanhamento,
      },
    },
    {
      field: meta?.situacaoRua?.visitaFamiliarComFrequencia.absolutePath(),
      updates: {
        [meta?.situacaoRua?.grauParentescoVisitaFamiliar?.absolutePath()]: (
          visitaFamiliarComFrequencia: SimNaoEnum,
          allValues: CidadaoFciFormModel
        ) =>
          allValues?.situacaoRua && visitaFamiliarComFrequencia !== SimNaoEnum.SIM
            ? null
            : allValues?.situacaoRua?.grauParentescoVisitaFamiliar,
      },
    },
  ]
}

const createResponsabilidadeAcompanhamentoCalculator = (meta: MetaRoot<CidadaoFciFormModel>): Calculation[] => {
  return [
    {
      field: meta?.responsabilidadeAcompanhamento.statusForaArea.absolutePath(),
      updates: {
        [meta?.responsabilidadeAcompanhamento?.microarea.absolutePath()]: (
          statusForaArea: boolean,
          allValues: CidadaoFciFormModel
        ) => (statusForaArea === true ? null : allValues?.responsabilidadeAcompanhamento?.microarea),
      },
    },
    {
      field: meta?.dadosPessoais?.faleceu?.absolutePath(),
      updates: {
        [meta?.responsabilidadeAcompanhamento?.statusCidadaoSaiuTerritorio.absolutePath()]: (
          faleceu: boolean,
          allValues: CidadaoFciFormModel
        ) => (faleceu === true ? null : allValues?.responsabilidadeAcompanhamento?.statusCidadaoSaiuTerritorio),
      },
    },
  ]
}

export const createCidadaoFciCalculator = (meta: MetaRoot<CidadaoFciFormModel>) =>
  createDecorator(
    ...createEtniaCalculator(meta),
    ...createNomeGenitoresCalculator(meta),
    ...createIdentidadeGeneroCalculator(meta),
    ...createOrientacaoSexualCalculator(meta),
    ...createNacionalidadeCalculator(meta),
    ...createObitoCalculator(meta),
    ...createInformacoesSociodemograficasCalculator(meta),
    ...createCondicoesSaudeAutorreferidasCalculator(meta),
    ...createSituacaoRuaCalculator(meta),
    ...createResponsabilidadeAcompanhamentoCalculator(meta)
  )
